import React, { forwardRef, useState, useEffect } from 'react';
import axios from '../../../../../services/AxiosInstance';
import { format } from 'date-fns';
import { getApiUrl } from '../../../../../utils/ApiUtils';

const InvoicePreviewBootstrap = forwardRef(({
  templateId,
  customer,
  company,
  invoiceNumber,
  invoiceDate,
  dueDate,
  items,
  notes,
  paymentInfo,
  paymentMethods = { bankTransfer: true, creditCard: false, cash: false },
  roundTotal = false,
  footerData: customFooterData
}, ref) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState(null);

  // Prepare invoice data for backend
  useEffect(() => {
    // Reset state when dependencies change
    setPdfUrl(null);
    setError(null);

    // Don't fetch if we don't have minimum required data
    if (!customer || !items.length) {
      return;
    }

    // Generate a request ID to help prevent race conditions
    const currentRequestId = Date.now().toString();
    setRequestId(currentRequestId);
    setIsLoading(true);

    // Prepare data for API
    const invoiceData = {
      templateId,
      customer,
      company,
      invoiceNumber,
      invoiceDate: format(invoiceDate, 'yyyy-MM-dd'),
      dueDate: format(dueDate, 'yyyy-MM-dd'),
      items,
      notes,
      paymentInfo,
      paymentMethods,
      roundTotal,
      footerData: customFooterData
    };

    // API call to generate PDF
    const fetchPdf = async () => {
      try {
        const response = await axios.post(
          getApiUrl('/api/invoices/preview-pdf'),
          invoiceData,
          { responseType: 'blob' }
        );

        // Only update state if this is still the current request
        if (requestId === currentRequestId) {
          // Create blob URL from response
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          setPdfUrl(url);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching PDF:', err);

        // Only update error state if this is still the current request
        if (requestId === currentRequestId) {
          setError('Det gick inte att generera PDF:en. Försök igen senare.');
          setIsLoading(false);
        }
      }
    };

    // Set a small delay to prevent too many requests during rapid changes
    const timer = setTimeout(() => {
      fetchPdf();
    }, 500);

    return () => {
      clearTimeout(timer);
      // Cleanup blob URL on unmount
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [
    templateId,
    customer?.id,
    company?.id,
    invoiceNumber,
    invoiceDate,
    dueDate,
    // We stringify these complex objects to properly detect changes
    JSON.stringify(items),
    notes,
    JSON.stringify(paymentMethods),
    roundTotal,
    JSON.stringify(customFooterData)
  ]);

  // If there's no customer or items, show placeholder
  if (!customer || items.length === 0) {
    return (
      <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded">
        <div className="text-center p-4">
          <h5 className="text-secondary mb-3">Faktura förhandsgranskning</h5>
          <p className="text-muted">
            Välj en kund och lägg till artiklar för att se förhandsgranskningen.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Laddar...</span>
          </div>
          <p className="text-muted">Genererar faktura förhandsgranskning...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded">
        <div className="text-center text-danger p-4">
          <div className="mb-3">
            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '2rem' }}></i>
          </div>
          <h5 className="mb-3">Det gick inte att generera förhandsgranskningen</h5>
          <p className="mb-4">{error}</p>
          <button
            className="btn btn-outline-primary"
            onClick={() => {
              setError(null);
              setRequestId(Date.now().toString());
              setIsLoading(true);
            }}
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  // Render PDF if we have a URL
  if (pdfUrl) {
    return (
      <div ref={ref} className="overflow-auto bg-light h-100 rounded">
        <div className="d-flex flex-column h-100">
          <object
            data={pdfUrl}
            type="application/pdf"
            className="w-100 h-100 rounded"
          >
            <div className="d-flex align-items-center justify-content-center h-100 bg-light p-4">
              <div className="text-center">
                <p className="mb-3">PDF-visaren stöds inte i din webbläsare.</p>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Öppna PDF i ny flik
                </a>
              </div>
            </div>
          </object>
        </div>
      </div>
    );
  }

  // Fallback state - waiting for PDF to be generated but not showing loader yet
  return (
    <div ref={ref} className="d-flex align-items-center justify-content-center h-100 bg-light rounded">
      <div className="text-center p-4">
        <p className="text-muted">Förbereder PDF-förhandsgranskning...</p>
      </div>
    </div>
  );
});

InvoicePreviewBootstrap.displayName = 'InvoicePreviewBootstrap';

export default InvoicePreviewBootstrap;
